import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import MetaControl from 'Components/UI/Meta/MetaControl';
import MetaCollectionFormInline from 'Components/UI/Meta/MetaForm/MetaCollectionFormInline';
import pluralize from 'pluralize';
import ErrorList from 'Components/UI/ErrorList';

const styles = theme => ({
  root: {
    width: '100%',
  },
  control: {
    display: 'flex',
    flex: '1',
    justifyContent: 'flex-start',
    width: '100%',
    marginBottom: theme.spacing(1),
    '& .MuiFormHelperText-root': {
      position: 'relative',
    },
    '& .MuiFormHelperText-root > span': {
      fontSize: theme.typography.h6.fontSize,
    },
  },
  controlNotFirstInRow: {
    paddingLeft: theme.spacing(1),
  },
  controlHidden: {
    display: 'none',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flex: '1',
  },
  errorList: {
    margin: props => (props?.errors?.length !== 0 ? theme.spacing(2, 0) : 0),
  },
  errorListHidden: {
    display: 'none',
  },
  section: {},
  title: {
    paddingBottom: theme.spacing(2),
  },
  nestedCollectionFormContainer: {
    width: '100%',
  },
  nestedCollectionForm: {
    width: '100%',
    paddingTop: 0,
    marginTop: 0,
  },
});

function MetaFormFieldList({
  className,
  classes,
  errors,
  hideEmptyFields,
  onChange,
  resource,
  rows,
  onAdd,
  onRemove,
  metaOverrideSet,
  hideErrorList,
  onErrorLinkClick,
  ErrorListProps,
}) {
  function hasInputError(template) {
    return (
      errors.filter(err => err.source === template.attr('name')).length > 0
    );
  }

  const addComponent = (template, columnIdx = 0) => {
    const attrName = template.attr('name');
    const hasError = hasInputError(template);
    const errorProps = {};

    if (hasError) {
      const error = errors.filter(
        err => err.source === template.attr('name'),
      )[0];
      errorProps.error = true;
      errorProps.helperText = error.shortDetail || error.detail;
      errorProps.helperTextColor = 'error';
    }

    if (template.attr('type') === 'array') {
      if (template.attr('hidden')) {
        return null;
      }

      let canAddMore = true;
      if (template.attr('max')) {
        canAddMore = resource.attr(attrName).resources().length() < template.attr('max');
      }
      let canRemove = true;
      if (template.attr('min')) {
        canRemove = resource.attr(attrName).resources().length() > template.attr('min');
      }

      return (
        <div className={classes.nestedCollectionFormContainer}>
          <MetaCollectionFormInline
            title={template.attr('prompt')}
            className={classes.nestedCollectionForm}
            collection={resource.attr(attrName)}
            onRemove={(onRemove && canRemove) ? onRemove : null}
            onAdd={(onAdd && canAddMore) ? () => {
              resource.attr(attrName).create({ addToCollection: true, useCollectionMetaOverrideSet: true });
              onAdd();
            } : null}
            addButtonText={onAdd && `Add ${pluralize(template.attr('prompt'), 1)}`}
            FormProps={{
              className: classes.nestedCollectionForm,
              noIndividualSubmitButtons: true,
              onAdd,
              onRemove,
            }}
            onErrorLinkClick={onErrorLinkClick}
          />
        </div>
      );
    }

    return (
      <MetaControl
        className={classNames(classes.control, {
          [classes.controlNotFirstInRow]: columnIdx > 0,
          [classes.controlHidden]: hideEmptyFields && resource.isAttrEmpty(attrName),
        })}
        attrName={attrName}
        error={errorProps.error}
        metaOverrideSet={metaOverrideSet}
        onChange={value => onChange(attrName, value)}
        resource={resource}
        key={attrName}
        HelperProps={{
          helperText: errorProps.helperText,
          color: errorProps.helperTextColor,
        }}
      />
    );
  };

  const resourceTemplate = resource.meta().template().getListOrderedByOrdinal(metaOverrideSet);

  return (
    <div className={classNames(classes.root, className)}>
      {rows.map((row, i) => {
        return (
          <div
            className={classes.row}
            key={`${resource.id()}-${i}`} // eslint-disable-line
          >
            {row.map((fieldName, j) => {
              const index = resourceTemplate.findIndex(
                temp => temp.attr('name') === fieldName,
              );
              const template = resourceTemplate[index];
              resourceTemplate.splice(index, 1);

              return addComponent(template, j);
            })}
          </div>
        );
      })}
      {resourceTemplate.map((template, i) => (
        <div
          className={classes.row}
          key={`${resource.id()}--${i}`} // eslint-disable-line
        >
          {addComponent(template)}
        </div>
      ))}
      <ErrorList
        className={classNames(classes.errorList, {
          [classes.errorListHidden]: hideErrorList,
        })}
        errors={errors}
        onErrorLinkClick={onErrorLinkClick}
        hideErrorTitle
        {...ErrorListProps}
      />
    </div>
  );
}

MetaFormFieldList.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  errors: PropTypes.array,
  hideEmptyFields: PropTypes.bool,
  hideErrorList: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  rows: PropTypes.array,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  metaOverrideSet: PropTypes.object,
  onErrorLinkClick: PropTypes.func,
  ErrorListProps: PropTypes.object,
};

MetaFormFieldList.defaultProps = {
  className: '',
  hideEmptyFields: false,
  errors: [],
  rows: [],
  onAdd: null,
  onRemove: null,
  metaOverrideSet: null,
  hideErrorList: false,
  onErrorLinkClick: null,
  ErrorListProps: undefined,
};

export default withStyles(styles, { name: 'MetaFormFieldList' })(
  MetaFormFieldList,
);
